import { useState } from "react";
import { ArrowUp, ArrowDown, Plus, X, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { computePrice, parseMoney, fmtRp } from "@/lib/price";

export const MoneyInput = ({ value, onChange, disabled, testid, className = "" }) => (
  <Input value={value == null ? "" : Number(value).toLocaleString("id-ID")} onChange={(e) => onChange(parseMoney(e.target.value))} readOnly={disabled}
    inputMode="numeric" placeholder="0" className={`tabular-nums ${disabled ? "bg-slate-50 text-slate-900 focus-visible:ring-0" : ""} ${className}`} data-testid={testid} />
);

const ItemRow = ({ it, i, last, p, set }) => {
  const upd = (patch) => set({ items: p.items.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const move = (dir) => { const items = [...p.items]; [items[i], items[i + dir]] = [items[i + dir], items[i]]; set({ items }); };
  const isPpn = it.type === "ppn";
  return (
    <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap" data-testid={`price-item-${i}`}>
      <div className="flex flex-col">
        <button type="button" disabled={i === 0} onClick={() => move(-1)} className="text-slate-400 hover:text-slate-900 disabled:opacity-20" aria-label="Naik" data-testid={`price-item-up-${i}`}><ArrowUp className="h-3.5 w-3.5" /></button>
        <button type="button" disabled={last} onClick={() => move(1)} className="text-slate-400 hover:text-slate-900 disabled:opacity-20" aria-label="Turun" data-testid={`price-item-down-${i}`}><ArrowDown className="h-3.5 w-3.5" /></button>
      </div>
      {it.type === "other"
        ? <Input value={it.label} onChange={(e) => upd({ label: e.target.value })} placeholder="Keterangan" className="h-9 min-w-0 flex-1" data-testid={`price-item-label-${i}`} />
        : <span className="min-w-0 flex-1 text-sm text-slate-700">{it.label}{isPpn && <span className="block text-[11px] text-slate-400">1,1% dari komponen di atasnya</span>}{isPpn && <label className="mt-1 flex items-center gap-1 text-xs text-slate-500"><Switch checked={p.ppn_auto} onCheckedChange={(v) => set({ ppn_auto: v })} data-testid="price-ppn-auto" />Otomatis</label>}</span>}
      <MoneyInput value={it.amount} onChange={(v) => upd({ amount: v })} disabled={isPpn && p.ppn_auto} className="h-9 w-28 sm:w-36" testid={`price-item-amount-${i}`} />
      {it.type === "other"
        ? <button type="button" onClick={() => set({ items: p.items.filter((_, j) => j !== i) })} className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600" aria-label="Hapus" data-testid={`price-item-remove-${i}`}><X className="h-4 w-4" /></button>
        : <span className="w-6" />}
    </div>
  );
};

export const PriceEditor = ({ price, onChange }) => {
  const [open, setOpen] = useState(false);
  const p = computePrice(price);
  const set = (patch) => onChange(computePrice({ ...p, ...patch }));
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 p-3" data-testid="price-editor">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="space-y-1.5"><Label className="text-xs">Harga Basic</Label><MoneyInput value={p.basic} onChange={(v) => set({ basic: v })} testid="price-basic" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Harga Setelah Discount</Label><MoneyInput value={p.discounted} onChange={(v) => set({ discounted: v })} testid="price-discounted" /></div>
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center justify-between gap-1"><Label className="text-xs">Total Harga</Label>
            <label className="flex items-center gap-1 text-[11px] text-slate-500"><Switch checked={p.total_auto} onCheckedChange={(v) => set({ total_auto: v })} data-testid="price-total-auto" />Otomatis</label>
          </div>
          <MoneyInput value={p.total} onChange={(v) => set({ total: v })} disabled={p.total_auto} className="font-semibold" testid="price-total" />
        </div>
      </div>
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between text-xs font-medium text-brand" data-testid="price-toggle-components">
        Rincian komponen harga (Fuel, Visa, PPN, Tipping, Asuransi, Lain-lain)
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-2 border-t border-slate-100 pt-3">
          {p.items.map((it, i) => <ItemRow key={i} it={it} i={i} last={i === p.items.length - 1} p={p} set={set} />)}
          <div className="flex items-center justify-between pt-1">
            <button type="button" onClick={() => set({ items: [...p.items, { type: "other", label: "", amount: null }] })} className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline" data-testid="price-add-other"><Plus className="h-3.5 w-3.5" /> Tambah Lain-lain</button>
            <span className="text-xs text-slate-500">Total otomatis: <b className="text-slate-800">{fmtRp(computePrice({ ...p, total_auto: true }).total)}</b></span>
          </div>
        </div>
      )}
    </div>
  );
};
